// imports
import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'

import cn from 'classnames'

import { RootState } from '@/utils/store/store'
import { Dropdown, Icon } from '@bosch/react-frok'

// components
import MinimalHeader from '@/ui/components/Header/CustomMinimalHeader/CustomMinimalHeader'
import Header from '@/ui/components/Header/CustomHeader/CustomHeader'
import Popover from '@/ui/components/Popover/Popover'
import Badge from '@/ui/components/Badge/Badge'
import CustomActions from '@/ui/components/Header/CustomActions/CustomActions'
import DocumentationFlyout from '@/ui/components/Flyout/Documentation/Documentation'
import User from '@/ui/components/Flyout/User'
import { VersionModalContent } from '@/ui/components/VersionHistory/VersionHistory'
import type { FlyoutInformation, AssistantMenuItem } from './types/HeaderTypes'
import type { PopoverPosition } from '@/ui/components/Popover/types/PopoverTypes'
import type { SideNavigationMenuGroup } from './SideNavigation/types/SideNavigationTypes'

// utils
import { HeaderType, MenuItemTypes } from '@/constants'
import { useModal } from '@/contexts/modalContext'
import { useSideNavMenu } from '@/contexts/sideNavContext'
import { useSpace } from '@/hooks/useSpace'
import { useNavigation } from '@/hooks/useNavigation'
import { useAuthentication } from '@/hooks/useAuthentication'
import useWindowSize from '@/hooks/useWindowSize'
import type Assistant from '@/utils/assistant/assistant'
import { AssistantConfig } from '@/shared/types/space'
import { DisplayMode } from '@/ui/types/DisplayMode'
import type { BadgeType } from '@/ui/components/Badge/types'

// styles
import './Header.scss'

// Badge per environment name; environments that are not listed (qa, prod) get none.
// The name is `environment` of /configs/config.json, which the deployment workflow sets
const ENVIRONMENT_BADGES: Record<string, BadgeType> = { dev: 'devSandbox' }

const BoschHeader = ({
    showMenu = true,
    showQuickLinks = true,
    headerType = HeaderType.DEFAULT
}) => {
    const [pageName, setPageName] = useState<React.ReactNode>(undefined)
    const [activeFlyout, setActiveFlyout] = useState<FlyoutInformation | null>(null)
    const [settingsModalActive, setSettingsModalActive] = useState<boolean>(false)

    const application = useSelector((state: RootState) => state.application)
    const config = useSelector((state: RootState) => state.application.config)
    const environment = useSelector((state: RootState) => state.application.rawConfig?.environment)
    const { lastUpdate } = useSelector((state: RootState) => state.assistants)
    const { information } = useSelector((state: RootState) => state.authentication)

    const hasUserName = !!information?.account?.name

    const { t, i18n } = useTranslation()
    const { navigateTo } = useNavigation()
    const { modal, showModal, hideModal } = useModal()
    const { logout, rehydrateInformation } = useAuthentication()
    const { spaces, getActiveSpace, getActiveAssistant, switchSpace, switchAssistant } = useSpace()
    const { isMobile } = useWindowSize()
    const { setMenuGroups } = useSideNavMenu()

    const activeSpace = getActiveSpace ? getActiveSpace() : undefined
    const activeAssistant = getActiveAssistant ? getActiveAssistant() : undefined
    const assistantsFromSpace = activeSpace?.assistants || []
    const className = cn({
        'chat-is-open ': !!pageName,
        'header-minimal': HeaderType.MINIMAL,
    })

    const closeActiveFlyout = () => setActiveFlyout(null)

    const onSpaceChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        // The preferred lines are kept per space: nothing to clear when switching spaces
        switchSpace(event.target.value)
    }

    const location = useLocation()

    useEffect(() => {
        if (!modal && settingsModalActive)
            setSettingsModalActive(false)
    }, [modal, settingsModalActive])

    useEffect(() => {
        setMenuGroups(getMenuItems())
        // The menu is rebuilt when the assistants, the config or the language change. getMenuItems is a new function in
        // every render and setMenuGroups renders this component again, so neither can be a dependency
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastUpdate, spaces, application, i18n.language])

    useEffect(() => {
        let nameTSX: React.ReactNode = null
        let titleToSet: string = ''

        if (activeSpace) {
            const { pathname } = location
            const id = activeAssistant?.config?.id
            const isHome = pathname === '/'
            const environmentBadge = environment ? ENVIRONMENT_BADGES[environment] : undefined

            const routeTitles: { [key: string]: string } = {
                '/': t('home:pageName'),
                '/feedback': t('feedback:title'),
                '/settings': t('general:settings:title'),
            }

            if (routeTitles[pathname]) {
                titleToSet = routeTitles[pathname]
            } else if (id) {
                const headerName = i18n.exists(`${id}:nameOnHeaderAndMenu`) ? t(`${id}:nameOnHeaderAndMenu`) : ''
                titleToSet = headerName || t(`${id}:name`)
            }

            nameTSX = (
                <div className='title-wrapper'>
                    <div className='mobile-header'>
                        <span>{titleToSet}</span>
                        {environmentBadge && <Badge badges={[environmentBadge]} mode={DisplayMode.DEFAULT} />}
                        {isHome && spaces.length > 1 && (
                            <div className='space-switch'>
                                <Dropdown
                                    className='switch -space'
                                    onChange={onSpaceChange}
                                    value={activeSpace?.id}
                                    options={spaces.map((space) => ({
                                        name: space.name,
                                        label: space.name,
                                        value: space.id,
                                    }))}
                                />
                                <Icon className='space-switch__icon' iconName='arrow-up-down' />
                            </div>
                        )}
                        {isMobile && (
                            <CustomActions 
                                showDetails={showDetails} 
                                isMobile={isMobile} 
                                user={information} 
                            />
                        )}
                    </div>
                </div>
            )
        }

        setPageName(nameTSX)

        // The title is built again when the page, the space, the assistant, the language or the layout change, not in
        // every render: setPageName renders this component again, and the closures it builds would be new every time
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname, activeSpace, activeAssistant, i18n.language, spaces, isMobile, environment])

    const openVersionHistoryModal = () => {
        showModal({
            title: t('general:header:whatsNew'),
            content: <VersionModalContent versionHistory={config?.history ?? []} />,
            confirmLabel: 'OK',
            onConfirmClick: async () => {
                hideModal()
            },
        })
    }

    const goHome = () => {
        navigateTo('/')
    }

    const goFeedback = () => {
        navigateTo('/feedback')
    }

    const goSettings = () => {
        navigateTo('/settings')
    }

    const goLogout = useCallback(() => {
        switchSpace(undefined)
        logout()
        navigateTo('/identity/login')
    }, [logout, navigateTo, switchSpace])

    const showDetails = (event: React.MouseEvent<HTMLElement>, target: string, position: PopoverPosition): void => {
        let modalInformation: FlyoutInformation | null = {
            type: target,
            content: null,
            sourceElement: event.currentTarget,
            position: position,
            customClass: ''
        }

        switch (true) {
            case target === 'documentation':
                modalInformation.content = (
                    <DocumentationFlyout
                        version={config?.history ? { history: config.history } : undefined}
                        onShowHistoryClick={() => {
                            closeActiveFlyout();
                            openVersionHistoryModal();
                        }} />)
                break
            case target === 'user':
                modalInformation.customClass = 'user-flyout'
                modalInformation.content = (
                    <User
                        user={information}
                        t={t}
                        navigateTo={(path: string) => {
                            closeActiveFlyout()
                            navigateTo(path)
                        }}
                        assistants={assistantsFromSpace}
                        rehydrateInformation={rehydrateInformation}
                        logout={goLogout}
                        switchSpace={switchSpace}
                    />
                )
                break
            default:
                modalInformation = null
                break
        }

        // the mobile header keeps an old copy of this function (it is built in an effect), so toggle on the latest state
        setActiveFlyout((current) => (current?.type === target ? null : modalInformation))
    }

    const getQuickLinks = (): { children: React.ReactNode }[] | null => {
        let quickLinks: { children: React.ReactNode }[] | null = null

        if (showQuickLinks) {
            quickLinks = [
                {
                    children: <CustomActions showDetails={showDetails} isMobile={isMobile} user={information} />
                }
            ]
        }

        return quickLinks
    }

    const onTileAction = async (assistant: Assistant): Promise<void> => {
        // to the page at once; the switch (the route asks for the same one) runs while the page shows the loading indicator
        navigateTo(`/${activeSpace?.path}/${assistant.path}`)
        await switchAssistant(assistant.config.id)
    }

    const assistantsMenu = (): AssistantMenuItem[] => {
        return activeSpace?.assistants?.map((assistant): AssistantMenuItem => {
            const { id } = assistant.config

            const headerName = i18n.exists(`${id}:nameOnHeaderAndMenu`) ? t(`${id}:nameOnHeaderAndMenu`) : ''
            const assistantConfig: AssistantConfig = assistant.config

            return {
                type: MenuItemTypes.item,
                label: headerName || t(`${id}:name`),
                icon: assistantConfig.appearance.icon,
                iconSrc: assistantConfig.appearance.iconSrc,
                action: () => onTileAction(assistant),
                pathHighlight: `/${activeSpace?.path}/${assistant.path}`,
                badges: assistantConfig.badges,
                translations: assistantConfig.translations,
            }
        }) || []
    }

    const getMenuItems = (): SideNavigationMenuGroup[] | null => {
        let menuItems: SideNavigationMenuGroup[] | null = null

        if (showMenu) {
            menuItems = [
                {
                    customClasses: 'full-size',
                    children: [
                        {
                            type: MenuItemTypes.item,
                            label: t('general:sideNavigation:home'),
                            icon: 'robot-body',
                            action: goHome,
                            pathHighlight: `/`
                        },
                        ...assistantsMenu()
                    ]
                },
                {
                    customClasses: '',
                    children: [
                        {
                            type: MenuItemTypes.group,
                            expandable: false,
                            children: [
                                {
                                    type: MenuItemTypes.item,
                                    label: t('feedback:title'),
                                    icon: 'thumb-up-down',
                                    action: goFeedback,
                                    pathHighlight: `/feedback`
                                },
                                {
                                    type: MenuItemTypes.item,
                                    label: t('general:settings:title'),
                                    icon: 'settings',
                                    action: goSettings,
                                    pathHighlight: `/settings`
                                }
                            ]
                        }
                    ]
                }
            ]
        }

        return menuItems
    }

    const getHeader = (): React.ReactNode => {
        let header: React.ReactNode = null

        switch (headerType) {
            case HeaderType.MINIMAL:
                header = (
                    <MinimalHeader
                        className={className}
                        quickLinks={getQuickLinks()}
                        pageName={pageName}
                    />
                )
                break
            case HeaderType.DEFAULT:
            default:
                header = (
                    <Header
                        className={className}
                        showMenu={showMenu}
                        showQuickLinks={showQuickLinks}
                        quickLinks={getQuickLinks()}
                        currentAssistantName={pageName}
                    />
                )
                break
        }

        return header
    }

    if (!hasUserName) return null

    return (
        <>
            {getHeader()}
            {activeFlyout && (
                <Popover
                    sourceElement={activeFlyout.sourceElement}
                    content={activeFlyout.content}
                    onClose={closeActiveFlyout}
                    position={activeFlyout.position}
                    positionMobile='bottom-left'
                    screenDistance={8}
                    customClass={activeFlyout.customClass}
                />
            )}
        </>
    )
}

export default BoschHeader
